import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const physicsUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Work, Energy, and Power",
  examWeight: "18–23%",
  focus: "The Energy Model: Analyzing motion through scalar transformations and conservation within defined systems.",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Translational Kinetic Energy",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏃 1. Definition and Formula</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Translational Kinetic Energy (<InlineMath math="K" />) is the energy associated with the motion of an object’s center of mass.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="K = \frac{1}{2}mv^2" />
              <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mt-2">
                <p><strong>m</strong> = mass (kg)</p>
                <p><strong>v</strong> = speed (m/s)</p>
              </div>
              <p className="text-xs text-blue-800 mt-2 font-semibold">Unit: Joule (J), where <InlineMath math="1\text{ J} = 1\text{ kg}\cdot\text{m}^2/\text{s}^2" />.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ 2. Key Characteristics</h3>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-slate-900">Scalar Quantity</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Kinetic energy has magnitude but no direction. It can <strong>never be negative</strong> because mass is positive and <InlineMath math="v^2" /> is always positive or zero.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-slate-900">Speed vs. Velocity</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Since <InlineMath math="K" /> depends on <InlineMath math="v^2" />, it depends only on <strong>speed</strong>, not direction. An object moving right at 5 m/s has the same <InlineMath math="K" /> as an object moving left at 5 m/s.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚉 3. Frame of Reference Dependence</h3>
            <p className="text-slate-700 text-sm leading-relaxed italic">
              Velocity is relative to the observer; therefore, <strong>Kinetic Energy is relative</strong>. An observer on a train sees a suitcase at rest (<InlineMath math="K=0" />), while an observer on the platform sees it moving (<InlineMath math="K > 0" />). Both are correct in their own inertial frames.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Work",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛠️ 1. Definition of Work</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Work (<InlineMath math="W" />) is the transfer of energy into or out of a system by a force exerted over a displacement. It is the "agent of change" for energy.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📐 2. Calculating Work (Constant Force)</h3>
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <BlockMath math="W = F d \cos\theta" />
              <ul className="list-disc pl-5 text-xs text-slate-700 space-y-1 mt-2">
                <li><strong>F</strong> = magnitude of force</li>
                <li><strong>d</strong> = magnitude of displacement</li>
                <li><strong>θ</strong> = angle between the force vector and displacement vector</li>
              </ul>
            </div>
            <p className="text-sm text-slate-600 mt-3">
              <strong>Component Analysis:</strong> Only the component of force <strong>parallel</strong> to the displacement does work: <InlineMath math="F_{\parallel} = F\cos\theta" />.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">➕ 3. Sign Conventions for Work</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-3 rounded-lg border border-green-200 bg-green-50">
                <h5 className="font-bold text-green-900 text-sm">Positive Work</h5>
                <p className="text-xs text-slate-700 mt-1"><InlineMath math="0^\circ \le \theta < 90^\circ" /></p>
                <p className="text-xs text-slate-600 mt-1">Force helps motion. Energy enters system. Speed increases.</p>
              </div>
              <div className="p-3 rounded-lg border border-red-200 bg-red-50">
                <h5 className="font-bold text-red-900 text-sm">Negative Work</h5>
                <p className="text-xs text-slate-700 mt-1"><InlineMath math="90^\circ < \theta \le 180^\circ" /></p>
                <p className="text-xs text-slate-600 mt-1">Force opposes motion. Energy removed. Speed decreases.</p>
              </div>
              <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                <h5 className="font-bold text-slate-900 text-sm">Zero Work</h5>
                <p className="text-xs text-slate-700 mt-1"><InlineMath math="\theta = 90^\circ" /></p>
                <p className="text-xs text-slate-600 mt-1">Force is perpendicular. No energy transfer (e.g., Centripetal force).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 4. Work Done by Variable Forces</h3>
            <KeyTerm 
              term="Graphical Analysis" 
              definition="On a Force vs. Position (F vs. x) graph, the area under the curve represents the Work done. Area above x-axis is positive; area below is negative."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📜 5. The Work-Energy Theorem</h3>
            <div className="bg-slate-900 text-white p-4 rounded-lg">
              <BlockMath math="W_{net} = \Delta K = K_f - K_i" />
            </div>
            <ul className="list-disc pl-6 mt-3 text-sm text-slate-700 space-y-1">
              <li><InlineMath math="W_{net} > 0" />: Object speeds up.</li>
              <li><InlineMath math="W_{net} < 0" />: Object slows down.</li>
              <li><InlineMath math="W_{net} = 0" />: Speed remains constant.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Potential Energy",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔋 1. Concept of Potential Energy</h3>
            <p className="text-slate-700 text-sm leading-relaxed mb-4">
              Potential Energy (<InlineMath math="U" />) is energy stored in a system due to the <strong>configuration</strong> (position) of objects.
            </p>
            <ImportantNote>
              <strong>Crucial Requirement:</strong> Potential energy exists <em>only</em> in a system of <strong>two or more objects</strong> interacting via a <strong>conservative force</strong>. A single object cannot have potential energy.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 2. Gravitational Potential Energy</h3>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Near Earth's Surface (<InlineMath math="U_g" />)</h4>
                <BlockMath math="U_g = mgy" />
                <p className="text-xs text-slate-700 mt-1">The choice of <InlineMath math="y=0" /> is arbitrary; only the <strong>change</strong> in height results in physical changes.</p>
              </div>
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-4 rounded-lg border border-slate-300">
                <h4 className="font-bold text-slate-900">General/Planetary (<InlineMath math="U_G" />)</h4>
                <BlockMath math="U_G = -\frac{G m_1 m_2}{r}" />
                <p className="text-xs text-slate-700 mt-1">Used for satellites. Defined as zero at <InlineMath math="r = \infty" />. It is negative everywhere else (attractive bound system).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏹 3. Elastic Potential Energy (Uₛ)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="U_s = \frac{1}{2} k x^2" />
              <p className="text-sm text-slate-700"><strong>Hooke’s Law:</strong> Restoring force is <InlineMath math="F_s = -kx" />. Work done is the area under the <InlineMath math="F" /> vs <InlineMath math="x" /> triangle.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ 4. Conservative vs. Non-Conservative Forces</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 p-3 rounded-lg shadow-sm">
                <h5 className="font-bold text-indigo-900 text-sm">Conservative</h5>
                <p className="text-xs text-slate-600 mt-1">Gravity, Springs. Path-independent. Energy can be stored as <InlineMath math="U" />. Work = <InlineMath math="-\Delta U" />.</p>
              </div>
              <div className="bg-white border border-slate-200 p-3 rounded-lg shadow-sm">
                <h5 className="font-bold text-rose-900 text-sm">Non-Conservative</h5>
                <p className="text-xs text-slate-600 mt-1">Friction, Air Resistance. Path-dependent. Dissipates energy into <strong>internal (thermal)</strong> energy.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Conservation of Energy",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📦 1. System Definition</h3>
            <p className="text-sm text-slate-700 mb-4">Defining the system determines if energy is internal or transferred via work.</p>
            <div className="space-y-2">
              <div className="bg-slate-50 p-3 rounded border border-slate-200 text-xs">
                <strong>Single Object:</strong> Only <InlineMath math="K" />. Gravity is an <em>external</em> force doing work.
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200 text-xs">
                <strong>Object-Earth:</strong> Both <InlineMath math="K" /> and <InlineMath math="U_g" />. Gravity is <em>internal</em>.
              </div>
              <div className="bg-slate-50 p-3 rounded border border-slate-200 text-xs">
                <strong>Object-Spring-Earth:</strong> Possesses <InlineMath math="K, U_g," /> and <InlineMath math="U_s" />.
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 2. Conservation Laws</h3>
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border border-teal-200">
              <h4 className="font-bold text-teal-900">Isolated Systems</h4>
              <p className="text-sm text-slate-700">No external work, only conservative interactions:</p>
              <BlockMath math="K_i + U_i = K_f + U_f" />
            </div>
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-lg border border-rose-200 mt-4">
              <h4 className="font-bold text-rose-900">Open Systems & Friction</h4>
              <BlockMath math="E_i + W_{ext} = E_f" />
              <p className="text-xs text-slate-700 mt-2">Energy dissipated by friction = <InlineMath math="|F_f d|" />.</p>
            </div>
          </section>

          <Mnemonic 
            title="Energy Bar Charts (LOL Diagrams)" 
            content="L (Initial Energy) → O (Work/Flow) → L (Final Energy). Total bars in Left + Work = Total bars in Right." 
          />
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Power",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ 1. Definition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Power (<InlineMath math="P" />) is the rate at which work is done or the rate at which energy is transferred/transformed.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⏱️ 2. Average Power</h3>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <BlockMath math="P_{avg} = \frac{\Delta E}{\Delta t} = \frac{W}{\Delta t}" />
              <p className="text-xs text-slate-600 text-center">Unit: Watt (W), where <InlineMath math="1\text{ W} = 1\text{ J/s}" />.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 3. Instantaneous Power</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <BlockMath math="P = F v \cos\theta" />
              <p className="text-xs text-slate-700 mt-2">Derived from <InlineMath math="P = W/t = (Fd)/t" />. Useful for objects moving at constant velocity against resistance (e.g., air drag).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "unit-3-tips",
      title: "AP Exam Tips & Common Misconceptions",
      content: (
        <div className="space-y-4">
          <div className="bg-indigo-900 text-indigo-50 p-6 rounded-xl shadow-lg">
            <h3 className="text-xl font-bold mb-4 border-b border-indigo-700 pb-2">💡 Pro-Tips for the Exam</h3>
            <ul className="space-y-4">
              <li className="text-sm">
                <span className="font-bold text-amber-400">1. System Choice:</span> On FRQs, if the system is "The Ball," gravity is <strong>Work</strong>. If the system is "Ball-Earth," gravity is <strong>Potential Energy</strong>. Both give the same math, but the explanation must match the system!
              </li>
              <li className="text-sm">
                <span className="font-bold text-amber-400">2. Work Signs:</span> Lifting a box? You do <strong>positive</strong> work (Force up, motion up). Gravity does <strong>negative</strong> work (Force down, motion up).
              </li>
              <li className="text-sm">
                <span className="font-bold text-amber-400">3. Universal PE Signs:</span> Remember <InlineMath math="U_G = -G\frac{m_1m_2}{r}" />. As objects get closer, <InlineMath math="U_G" /> becomes <strong>more negative</strong>. To escape a planet, <InlineMath math="E_{total} \ge 0" />.
              </li>
              <li className="text-sm">
                <span className="font-bold text-amber-400">4. Graph Slopes:</span> 
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  <li>Slope of <strong>Energy vs. Time</strong> = Power.</li>
                  <li>Slope of <strong>Potential Energy vs. Position</strong> = Negative Force (<InlineMath math="F = -dU/dx" />).</li>
                </ul>
              </li>
              <li className="text-sm">
                <span className="font-bold text-amber-400">5. Pendulums:</span> At the bottom, acceleration is <strong>not zero</strong>; it is centripetal (<InlineMath math="v^2/r" />) directed upward. Tension is greater than gravity.
              </li>
            </ul>
          </div>
        </div>
      )
    }
  ]
};

export default physicsUnit3Notes;